import { FC, useEffect, useMemo, useRef } from "react";
// 组件
import { Table } from 'antd';
import { ProTable } from '../antd';
import EditableRow from './subpage/editableRow';
import EditableCell from './subpage/editableCell';
import TotalTable, { TotalTableActionType } from "./subpage/totalTable";
// 方法
import classNames from "classnames";
import MainSubEdiTableContext from './context';
import useMainSubEdiTableContext from './hooks/useMainSubEdiTableContext';
import useMainSubEdiTableColumnsHook from './hooks/useMainSubEdiTableColumnsHook';
import { formatFormData, getFieldInfo } from "./util";
import { filterObjEmptyValue } from "../utils/utils";
// 常量
import { MainSubEdiTableProps } from './interface';
import { FormInstance } from "antd/lib/form";
import { STATUS_CREATED, STATUS_CREATED_CODE } from "../constants/constant";
import { IObject } from "../constants/interface";
import './style.less';

/** 通过合并列实现的主子可编辑表 */
const MainSubEdiTable: FC<MainSubEdiTableProps> = props => {
  const { actionRef, maxHeight, childrenKey = 'key', totalKeys, size, totalIndex, totalText, onValuesChange } = props;
  const forms = useRef<FormInstance[][]>([]);
  const flagIndex = useRef<number>(0);
  const totalTableRef = useRef<TotalTableActionType>();
  const { dispatchEdiTable, dataSource, } = useMainSubEdiTableContext(flagIndex, props.dataSource, forms);
  const { columns } = useMainSubEdiTableColumnsHook(props, dispatchEdiTable, dataSource,);
  useEffect(() => {
    handleChangeTotal(dataSource);
  }, [dataSource]);
  /** 字段信息 */
  const fieldInfo = useMemo(() => {
    return getFieldInfo(props.columns);
  }, [props.columns]);
  /** 表单字段值改变 */
  const handleValuesChange = (
    value: any,
    changedValues: any,
    rowValues: any,
    index: number,
    form: FormInstance,
  ) => {
    const changedFieldname = Object.keys(changedValues)?.[0];
    // 是否需要更新总计
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const findItem = totalKeys.find(v => v.dataIndex === changedFieldname);
      if (findItem) {
        handleUpdateTotalData();
      }
    }
    onValuesChange?.(value, changedValues, rowValues, index, form);
  };
  /** 新增行 */
  const handleAddRow = (addItem?: any, isEmpty?: boolean) => {
    dispatchEdiTable('addRow', { addItem, isEmpty });
  };
  /** 添加子表行 */
  const handleChildrenAdd = (record: any, addItem?: IObject | IObject[]) => {
    dispatchEdiTable('addChildren', { mainKey: record.parentKey, addItem });
  };
  /** 删除行 */
  const handleDeleteRow = (record: any) => {
    dispatchEdiTable('deleteRow', { record: record });
  };
  const mainSubDataSource = useMemo(() => {
    const flatDataSource: any[] = [];
    if (Array.isArray(dataSource) && dataSource.length) {
      dataSource.forEach(item => {
        const { children, key, ...restProps } = item;
        if (Array.isArray(children) && children.length) {
          children.forEach((v, k) => {
            flatDataSource.push({...restProps, ...v, key: `${key}-${v[childrenKey] || k}`, parentKey: key, rowSpan: k === 0 ? children.length : 0, childrenKey: k});
          })
        } else {
          flatDataSource.push({...restProps, key: key, parentKey: key, childrenKey: 0, rowSpan: 1});
        }
      });
    }
    return flatDataSource;
  }, [dataSource]);
  /** 获取当前表格数据 */
  const handleGetDataSource = () => {
    const currentDataSource: any[] = [];
    if (!dataSource) return currentDataSource;
    dataSource.forEach(item => {
      const itemKey = item.key;
      const itemChildren = item.children;
      const firstItemFormValues = forms.current[itemKey]?.[0].getFieldsValue(true);
      let newItem: any = {};
      if (Array.isArray(itemChildren)) {
        newItem = {
          ...item,
          ...(formatFormData(firstItemFormValues, fieldInfo, false, false, false)),
          children: itemChildren.map((v, k) => {
            const vFormValues = forms.current[itemKey][k]?.getFieldsValue(true);
            return {
              ...v,
              ...(formatFormData(vFormValues, fieldInfo, false, true, false)),
            };
          }),
        };
      } else {
        newItem = {...item, ...(formatFormData(firstItemFormValues, fieldInfo, false, false, false)),};
      }
      currentDataSource.push(newItem);
    });
    return currentDataSource;
  };
  /**
   * 提交
   * @param filterEmptyValue 是否过滤表单为空的值
   * @returns 
   */
  const handleSubmit = async (filterEmptyValue: boolean = true) => {
    if (!dataSource) return [];
    let flag = true;
    const newDataSource: any[] = [];
    let newGroup: any = {};
    for (let i = 0, len = dataSource.length; i < len; i++) {
      try {
        const group = dataSource[i];
        const groupChildren = group.children;
        const groupForm = forms.current?.[group.key]?.filter(v => !!v);
        const firstItemValues = await groupForm?.[0].validateFields();
        if (Array.isArray(groupChildren)) {
          const newGroupChildren: any[] = [];
          for (let n = 0, nLen = groupChildren.length; n < nLen; n++) {
            const { key: childrenItemKey, ...restChildrenItem } = groupChildren[n];
            const childrenForm = groupForm[childrenItemKey];
            const childrenValues = await childrenForm.validateFields();
            newGroupChildren.push(filterEmptyValue ? filterObjEmptyValue({
              ...restChildrenItem,
              ...(formatFormData(childrenValues, fieldInfo, false, true)),
            }) : {
              ...restChildrenItem,
              ...(formatFormData(childrenValues, fieldInfo, false, true)),
            });
          }
          newGroup = {
            ...(filterEmptyValue ? filterObjEmptyValue({
              ...group,
              ...(formatFormData(firstItemValues, fieldInfo, false, false)),
            }) : {
              ...group,
              ...(formatFormData(firstItemValues, fieldInfo, false, false)),
            }),
            children: newGroupChildren
          };
        } else {
          newGroup = filterEmptyValue ? filterObjEmptyValue({
            ...group,
            ...(formatFormData(firstItemValues, fieldInfo, false, false)),
          }) : {
            ...group,
            ...(formatFormData(firstItemValues, fieldInfo, false, false)),
          };
        }
        delete newGroup.key
        newDataSource.push(newGroup);
      } catch (errorInfo) {
        flag = false;
      }
    }
    return flag ? newDataSource : false;
  };
  /** 清空 */
  const handleEmpty = () => {
    dispatchEdiTable('changeDataSource', {updateDataSource: []});
  };
  /** 更新统计数据 */
  const handleUpdateTotalData = () => {
    if (Array.isArray(totalKeys) && totalKeys.length) {
      const effectiveDataSource = handleGetDataSource().filter(
        v => v[STATUS_CREATED_CODE] !== STATUS_CREATED.DELETE,
      );
      totalTableRef.current?.onChange(effectiveDataSource);
    }
  };
  /** 手动传入表格数据进行统计数据更新 */
  const handleChangeTotal = (tableData?: any[]) => {
    if (tableData) {
      totalTableRef.current?.onChange(tableData);
    } else {
      handleUpdateTotalData();
    }
  };
  const tableClasses = useMemo(() => {
    return classNames("yb-main-sub-edit-table", `yb-main-sub-edit-table-${size}`);
  }, [size]);
  if (actionRef) {
    actionRef.current = {
      add: handleAddRow,
      addChildren: handleChildrenAdd,
      delete: handleDeleteRow,
      getDataSource: handleGetDataSource,
      changeTotal: handleChangeTotal,
      submit: handleSubmit,
      empty: handleEmpty,
      forms: forms.current,
    };
  }
  return (
    <MainSubEdiTableContext.Provider
      value={{
        dataSource,
        forms,
        dispatchEdiTable,
      }}
    >
      <ProTable
        className={tableClasses}
        dataSource={mainSubDataSource}
        pagination={false}
        columns={columns}
        bordered
        size="small"
        components={{
          body: {
            row: EditableRow,
            cell: EditableCell,
          },
        }}
        // @ts-ignore
        onRow={(record) => {
          return {
            record,
            onValuesChange: handleValuesChange,
          };
        }}
        scroll={{
          scrollToFirstRowOnChange: true,
          y: maxHeight,
        }}
        summary={() => {
          if (
            Array.isArray(totalKeys) &&
            !!totalKeys.length &&
            Array.isArray(mainSubDataSource) &&
            !!mainSubDataSource.length
          ) {
            return (
              <Table.Summary fixed>
                <TotalTable
                  actionRef={totalTableRef}
                  columns={columns}
                  totalKeys={totalKeys}
                  totalIndex={totalIndex}
                  totalText={totalText}
                />
              </Table.Summary>
            );
          }
        }}
      />
    </MainSubEdiTableContext.Provider>
  );
};

MainSubEdiTable.defaultProps = {
  childrenKey: 'key',
  maxHeight: 300,
  size: 'middle',
};

export default MainSubEdiTable;
